/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import { result } from 'underscore';
import config from '@bingads-webui-component-legacy/config/index';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { inlineUpdateTasks } from './data-source';

const getScheduleTaskIds = (campaignList, key, notificationCache = {}) => {
  const taskIdMap = {};
  campaignList.forEach((campaign) => {
    if (!campaign.ImportProperties) {
      return;
    }
    if (campaign.ImportProperties.ScheduledTaskList) {
      campaign.ImportProperties.ScheduledTaskList.forEach((task) => {
        const bitMasks = task.OptionBitMask ? task.OptionBitMask.split(', ') : [];
        if (bitMasks.includes(key) && !(notificationCache[task.TaskId])) {
          taskIdMap[task.TaskId] = true;
        }
      });
    }
  });
  return Object.keys(taskIdMap);
};

const getNotificationMessage = (i18n, key, handleLinkClick) => {
  const handleLink = (e) => {
    e.preventDefault();
    handleLinkClick();
  };

  return (
    <ReactTemplate
      template={i18n.getString(key)}
      model={{
        redirectLink: text => (
          <a
            role="button"
            onClick={e => handleLink(e)}
          >
            {text}
          </a>
        ),
        bold: text => <b>{text}</b>,
      }}
    />
  );
};

/**
 * When campaign/campaigns is/are paused/enabled, judge if we need to show the notification
 * @param {object} campaignItemList, the list of campaigns with campaign data, required
 * @param {string} updateStatus, the updated campaign status, required
 * @param {object} legacyI18n, legacy i18n, not required but need to be passed in if window.globalLegacyI18n does not work, e.g. smart campaigns page
 * @param {object} Permissions, permissions, not required but need to be passed in if config.get('Permissions') does not work, e.g. smart campaigns page
 * @param {object} Scope, scope about uid/cid/aid, not required but need to be passed in if config.get('CurrentUser') does not work, e.g. smart campaigns page
 * @returns {object} No returns, call notificationApi to show notification if the condition is met
 */
export const showPausedCampaignNotifications = ({
  campaignItemList,
  updateStatus,
  activity = { trace: () => {} },
  legacyI18n = null,
  Permissions = null,
  Scope = null,
}) => {
  const permissions = Permissions || config.get('Permissions') || {};
  const isImportPausedCampaignNotificationEnabled = permissions && permissions.dynamic && permissions.dynamic.ImportPausedCampaignNotification;

  if (!window.ImportCampaignStatusNotificationTasks) {
    window.ImportCampaignStatusNotificationTasks = {};
  }

  activity.trace(JSON.stringify({
    event: 'StatusChange',
    isImportPausedCampaignNotificationEnabled,
    updateStatus,
  }));

  // if pilot on and has any campaign item related with active scheduled campaigns
  const scheduledTaskList = isImportPausedCampaignNotificationEnabled ? getScheduleTaskIds(campaignItemList, 'UpdateCampaignStatus', window.ImportCampaignStatusNotificationTasks) : [];

  if (scheduledTaskList.length > 0) {
    const i18n = legacyI18n || window.globalLegacyI18n.get('component/i18n/loader!legacy/global');
    const scope = Scope || {
      aid: result(config.get('CurrentAccount'), 'Id'),
      cid: result(config.get('CurrentCustomer'), 'Id'),
    };
    let notificationId = 0;

    // show different notification message according to user's action
    const notificationMessage = getNotificationMessage(i18n, 'Campaign_Status_Change_Overwrite_Notification_Message', async () => {
      activity.trace(JSON.stringify({
        event: 'Resolve',
        updateStatus,
        taskIds: scheduledTaskList,
      }));
      const res = await inlineUpdateTasks(scope, scheduledTaskList.map(v => ({
        taskId: v,
        preference: {
          UpdateStatusForCampaigns: false,
        },
      })));
      notificationApi.dismiss(notificationId);
      if (res.statusCounter && res.statusCounter.success === scheduledTaskList.length) {
        scheduledTaskList.forEach((v) => {
          window.ImportCampaignStatusNotificationTasks[v] = true;
        });
        notificationApi.showInfo(i18n.getString('Import_Preference_InlineUpdate_Success'));
      } else {
        notificationApi.showWarning(i18n.getString('Import_Preference_InlineUpdate_Error'));
      }
    });

    const uniqueFloatingBannerId = `FloatingBanner-CampaignStatusOverwrite-${scope.aid}`;

    activity.trace(JSON.stringify({
      event: 'Show',
      updateStatus,
      dismissed: !!window.sessionStorage.getItem(uniqueFloatingBannerId),
      taskIds: scheduledTaskList,
    }));

    // Used in paused-schedule-dialog.js, if user pause/delete tasks within 1 hour after this notification display, there will be inline edit button display on the dialog
    window.sessionStorage.setItem(`ImportedCampaignStatusOverwrite-${scope.aid}`, Date.now().toString());

    // use session mode dismiss for floating banner based on account id
    notificationId = notificationApi.showWarning(notificationMessage, {
      sessionModeDismissForFloatingBannerProps: {
        uniqueFloatingBannerId,
      },
      onDismiss: () => {
        activity.trace(JSON.stringify({
          event: 'Dismiss',
          updateStatus,
        }));
      },
    });
  }
};

export const showBudgetBidCampaignNotifications = ({
  campaignItemList,
  oldBudget,
  newBudget,
  oldBidStrategy,
  newBidStrategy,
  activity = { trace: () => {} },
  legacyI18n = null,
  Permissions = null,
  Scope = null,
}) => {
  let hasBudgetChange = false;
  let hasBidStrategyChange = false;

  if (newBudget) {
    if (oldBudget) {
      Object.keys(newBudget).forEach((key) => {
        if (newBudget[key] && oldBudget[key] !== newBudget[key]) {
          hasBudgetChange = true;
        }
      });
    } else {
      // Bulk edit scenario have no oldBudget
      hasBudgetChange = true;
    }
  }

  if (newBidStrategy) {
    if (oldBidStrategy) {
      Object.keys(newBidStrategy).forEach((key) => {
        if (newBidStrategy[key] && oldBidStrategy[key] !== newBidStrategy[key]) {
          hasBidStrategyChange = true;
        }
      });
    } else {
      // Bulk edit scenario have no oldBidStrategy
      hasBidStrategyChange = true;
    }
  }

  const permissions = Permissions || config.get('Permissions') || {};
  const isImportBudgetBidStrategyForCampaignNotificationEnabled = permissions && permissions.dynamic && permissions.dynamic.ImportBudgetBidStrategyForCampaignNotification;
  const budgetChangeTaskList = (hasBudgetChange && isImportBudgetBidStrategyForCampaignNotificationEnabled) ? getScheduleTaskIds(campaignItemList, 'UpdateCampaignBudget') : [];
  const bidStrategyChangeTaskList = (hasBidStrategyChange && isImportBudgetBidStrategyForCampaignNotificationEnabled) ? getScheduleTaskIds(campaignItemList, 'UpdateBiddingStrategies') : [];

  if (budgetChangeTaskList.length === 0 && bidStrategyChangeTaskList.length === 0) {
    return;
  }

  activity.trace(JSON.stringify({
    event: 'BudgetBidChange',
    isImportBudgetBidStrategyForCampaignNotificationEnabled,
    oldBudget,
    newBudget,
    oldBidStrategy,
    newBidStrategy,
  }));

  const i18n = legacyI18n || window.globalLegacyI18n.get('component/i18n/loader!legacy/global');
  const scope = Scope || {
    aid: result(config.get('CurrentAccount'), 'Id'),
  };

  let message = 'Campaign_Budget_And_BidStrategy_Change_Overwrite_Notification_Message';
  let uniqueFloatingBannerId = `FloatingBanner-CampaignBudgetBidStrategyOverwrite-${scope.aid}`;
  if (budgetChangeTaskList.length === 0) {
    message = 'Campaign_BidStrategy_Change_Overwrite_Notification_Message';
    uniqueFloatingBannerId = `FloatingBanner-CampaignBidStrategyOverwrite-${scope.aid}`;
  } else if (bidStrategyChangeTaskList.length === 0) {
    message = 'Campaign_Budget_Change_Overwrite_Notification_Message';
    uniqueFloatingBannerId = `FloatingBanner-CampaignBudgetOverwrite-${scope.aid}`;
  }

  // show different notification message according to user's action
  const notificationMessage = getNotificationMessage(i18n, message, () => activity.trace(JSON.stringify({
    event: 'Redirect',
    oldBudget,
    newBudget,
    oldBidStrategy,
    newBidStrategy,
  })));

  activity.trace(JSON.stringify({
    event: 'Show',
    oldBudget,
    newBudget,
    oldBidStrategy,
    newBidStrategy,
    dismissed: !!window.sessionStorage.getItem(uniqueFloatingBannerId),
  }));

  // use session mode dismiss for floating banner based on account id
  notificationApi.showWarning(notificationMessage, {
    sessionModeDismissForFloatingBannerProps: {
      uniqueFloatingBannerId,
    },
    onDismiss: () => {
      activity.trace(JSON.stringify({
        event: 'Dismiss',
        oldBudget,
        newBudget,
        oldBidStrategy,
        newBidStrategy,
      }));
    },
  });
};
